// KML rendering scaffolding shared by the track map (map-base.js) and the MapLayer admin
// preview (map.mjs -> window.GSatKmlOverlays): guarded XML parse, Style/StyleMap extraction,
// icon-style attachment, the L.geoJSON option builders (popups/tooltips, icon markers,
// per-feature styling), and GroundOverlay construction. The pure parsing helpers live in
// kml-overlays.mjs; this module is browser-only (jQuery, redom, and Leaflet are reached via
// window globals so it can be bundled into both the tracking and map bundles without
// duplicating those libraries).

import { parseIconHrefs, parseGroundOverlays, resolveLayerHref } from "./kml-overlays.mjs";

// KML feature properties that are styling metadata, not user data, so the fallback
// properties-table popup skips them.
var EXCLUDED_PROPERTIES = ["stroke", "stroke-opacity", "fill-opacity", "styleUrl", "styleHash"];

// $.parseXML wrapped so malformed KML yields null instead of throwing (callers skip the layer).
export function parseKmlDocument(kmlText) {
	try {
		return window.jQuery.parseXML(kmlText);
	} catch (e) {
		return null;
	}
}

// Extracts <Style>/<StyleMap> definitions into an id-keyed dictionary of Leaflet path options.
// When `into` is provided it is filled and returned (callers whose geoJSON option closures
// already captured the dictionary need in-place population); otherwise a new one is created.
export function parseKmlStyles(data, into) {
	var $ = window.jQuery;
	var styles = into || {};
	$(data)
		.find("Style")
		.each(function () {
			var item = $(this);
			// may have multiple styles associated with it
			// so we should prioritize via PolyStyle, LineStyle, IconStyle, LabelStyle
			var style = {
				// defaults
				fillColor: null,
				fillOpacity: 0.2,
				color: "#3388ff",
				opacity: 1.0,
				weight: 3,
			};
			// parse KML styles, which should be in abgr format
			var fillColor = item.find("PolyStyle color").text();
			var strokeColor = item.find("LineStyle color").text();
			var strokeWeight = item.find("LineStyle width").text();
			var iconScale = item.find("IconStyle scale").text();
			if (iconScale !== "" && !isNaN(parseFloat(iconScale))) {
				style.radius = 10 * parseFloat(iconScale);
			}
			// weight must be numeric: Leaflet does arithmetic on options.weight
			// (Canvas._extendRedrawBounds, Polygon._clipPoints) and a string from the
			// KML <width> text concatenates instead, corrupting the redraw/clip bounds
			// so paths silently stop (re)drawing until a full moveend redraw.
			if (strokeWeight !== "" && !isNaN(parseFloat(strokeWeight))) {
				style.weight = parseFloat(strokeWeight);
			}
			if (strokeColor === "") {
				strokeColor = item.find("IconStyle color").text();
			}
			if (strokeColor === "") {
				strokeColor = item.find("LabelStyle color").text();
			}
			if (strokeColor !== "") {
				// parse opacity
				style.color = "#" + strokeColor.substring(6, 8) + strokeColor.substring(4, 6) + strokeColor.substring(2, 4);
				style.opacity = parseInt(strokeColor.substring(0, 2), 16) / 255;
			}
			if (fillColor !== "") {
				style.fillColor = "#" + fillColor.substring(6, 8) + fillColor.substring(4, 6) + fillColor.substring(2, 4);
				style.fillOpacity = parseInt(fillColor.substring(0, 2), 16) / 255;
				if (strokeColor === "") {
					style.color = style.fillColor;
					style.opacity = style.fillOpacity;
				}
			}
			// non-KML
			if (strokeColor === "") {
				style.color = item.find("color").text();
			}
			if (style.color === "") {
				style.color = "#ffffff"; // default isn't always populated
			}

			var styleId = item.attr("id");
			if (styleId == null) {
				// Google Earth (web) wraps styles as <gx:CascadingStyle kml:id="..."> with an
				// anonymous inner <Style>; the id the placemarks reference lives on the wrapper.
				var styleParent = item.parent();
				if (styleParent.length > 0 && styleParent[0].localName === "CascadingStyle") {
					styleId = styleParent.attr("kml:id");
				}
			}
			if (styleId != null) {
				styles[styleId] = style;
			}
		});

	$(data)
		.find("StyleMap")
		.each(function () {
			var item = $(this);
			styles[item.attr("id")] = styles[item.find("styleUrl").eq(0).text().substr(1)];
		});

	return styles;
}

// Attaches extracted-KMZ icon images to their styles. StyleMap aliases share the same style
// object, so mutating here covers them too. External (non-https, non-archive) hrefs are
// dropped and those points keep the circle-marker rendering. `resolveUrl` optionally
// post-processes each resolved URL (the track map wraps root-relative URLs for shared views).
export function attachKmlIconStyles(styles, kmlText, baseUrl, resolveUrl) {
	var iconHrefs = parseIconHrefs(kmlText);
	for (var styleId in iconHrefs) {
		if (Object.prototype.hasOwnProperty.call(iconHrefs, styleId) && styles[styleId]) {
			var resolvedIconUrl = resolveLayerHref(iconHrefs[styleId].href, baseUrl, false);
			if (resolvedIconUrl !== null && resolveUrl) {
				resolvedIconUrl = resolveUrl(resolvedIconUrl);
			}
			if (resolvedIconUrl !== null) {
				styles[styleId].iconUrl = resolvedIconUrl;
				styles[styleId].iconScale = iconHrefs[styleId].scale;
			}
		}
	}
}

// Builds the L.geoJSON options for KML vectors: name tooltip + name/description popup (falling
// back to a properties table), icon or styled-circle markers for points, and per-feature path
// styling — all looked up in `styles` at parse time, so the dictionary may be populated after
// the layer is constructed but before features are added. options: { pane } (track map renders
// into its back-lines pane; the admin preview uses the default panes).
export function buildKmlGeoJsonOptions(styles, options) {
	var el = window.redom.el;
	var text = window.redom.text;
	var L = window.L;
	var geoJsonOptions = {
		onEachFeature: function (feature, layer) {
			if (feature.properties != null) {
				var hasPopup = false;
				if (feature.properties.name != null) {
					layer.bindTooltip(feature.properties.name);
					if (feature.properties.description != null) {
						layer.bindPopup(el("div", [el("h6", feature.properties.name), text(feature.properties.description)]));
						hasPopup = true;
					}
				}
				if (!hasPopup) {
					var rows = [];
					for (var key in feature.properties) {
						if (!Object.prototype.hasOwnProperty.call(feature.properties, key)) {
							continue;
						}
						if (EXCLUDED_PROPERTIES.indexOf(key) !== -1) {
							continue;
						}
						rows.push(el("tr", [el("td", key), el("td", feature.properties[key])]));
					}
					if (rows.length > 0) {
						layer.bindPopup(el("table.feature-properties", el("tbody", rows)));
					}
				}
			}
		},
		pointToLayer: function (feature, latlng) {
			// check for custom styling for the placemark
			if (feature.properties != undefined && feature.properties.styleUrl != undefined) {
				// == null covers undefined too: an unregistered style id must fall back to the
				// default marker, not crash on the property reads below.
				var iconStyle = styles[feature.properties.styleUrl.substr(1)];
				if (iconStyle == null) {
					// map be using a style map
					iconStyle = styles[feature.properties.styleUrl.substr(1) + "-normal"];
				}
				if (iconStyle != null && iconStyle.iconUrl) {
					var iconScale = (iconStyle.iconScale == null || isNaN(iconStyle.iconScale)) ? 1 : iconStyle.iconScale;
					var iconSize = Math.round(32 * iconScale);
					return L.marker(latlng, {
						icon: L.icon({
							iconUrl: iconStyle.iconUrl,
							iconSize: [iconSize, iconSize],
							iconAnchor: [iconSize / 2, iconSize / 2],
						}),
					});
				}
				if (iconStyle != null) {
					// with placemarks, GoogleEarth sets both a PolyStyle and an IconStyle with the PolyStyle having a fill of black
					// so we should ignore fillColor for them and set the fillColor to the strokeColor at a lower opacity
					if (iconStyle.color !== null) {
						iconStyle.fillColor = iconStyle.color;
						if (iconStyle.opacity !== null) {
							iconStyle.fillOpacity = iconStyle.opacity * 0.5;
						}
					}
					return L.circleMarker(latlng, iconStyle);
				}
			}
			return L.marker(latlng);
		},
		style: function (feature) {
			if (feature.properties != undefined && feature.properties.styleUrl != undefined) {
				var layerStyle = styles[feature.properties.styleUrl.substr(1)];
				if (layerStyle === undefined) {
					// map be using a style map
					layerStyle = styles[feature.properties.styleUrl.substr(1) + "-normal"];
				}
				if (layerStyle !== undefined) {
					return layerStyle;
				}
			}
		},
	};
	if (options && options.pane) {
		geoJsonOptions.pane = options.pane;
	}
	return geoJsonOptions;
}

// Builds L.imageOverlay instances for the KML's GroundOverlays (extracted-KMZ relative hrefs,
// or https absolute subject to CSP), sorted by drawOrder. rotation is rendered un-rotated for
// now (documented limitation). options: { resolveUrl, pane } as above.
export function buildKmlGroundOverlays(kmlText, baseUrl, options) {
	var L = window.L;
	var groundOverlays = parseGroundOverlays(kmlText);
	var overlays = [];
	for (var i = 0; i < groundOverlays.length; i++) {
		var overlayDef = groundOverlays[i];
		var overlayUrl = resolveLayerHref(overlayDef.href, baseUrl, true);
		if (overlayUrl !== null && options && options.resolveUrl) {
			overlayUrl = options.resolveUrl(overlayUrl);
		}
		if (overlayUrl === null) {
			continue;
		}
		var overlayBounds = L.latLngBounds(
			L.latLng(overlayDef.south, overlayDef.west),
			L.latLng(overlayDef.north, overlayDef.east));
		var overlayOptions = {
			opacity: overlayDef.opacity,
			drawOrder: overlayDef.drawOrder,
		};
		if (options && options.pane) {
			overlayOptions.pane = options.pane;
		}
		overlays.push(L.imageOverlay(overlayUrl, overlayBounds, overlayOptions));
	}
	overlays.sort(function (a, b) {
		return (a.options.drawOrder || 0) - (b.options.drawOrder || 0);
	});
	return overlays;
}
